import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
}

export default function Testimonial() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);

  const projects = [
    {
      icon: 'ri-window-line',
      name: 'Plataforma Web Personalizada',
      role: 'Aplicación Web',
      content: 'Diseño y desarrollo de un sistema web a la medida enfocado en optimizar procesos internos y mejorar la experiencia del usuario final.',
    },
    {
      icon: 'ri-settings-3-line',
      name: 'Automatización Empresarial',
      role: 'Sistema Interno',
      content: 'Implementación de soluciones digitales que eliminan las "Tareas Zombi", reducen errores y mejoran la eficiencia operativa.',
    },
    {
      icon: 'ri-shopping-cart-line',
      name: 'E-commerce Escalable',
      role: 'Comercio Digital',
      content: 'Desarrollo de arquitectura preparada para alto tráfico, con enfoque en estabilidad, seguridad y crecimiento sostenible.',
    }
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = canvas.parentElement?.offsetHeight || 700;
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    // Partículas consistentes con el resto del sitio
    particlesRef.current = Array.from({ length: 20 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.25,
      vy: (Math.random() - 0.5) * 0.25,
      radius: Math.random() * 1.2,
      color: '#00C2FF'
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach(p => {
        p.x = (p.x + p.vx + canvas.width) % canvas.width;
        p.y = (p.y + p.vy + canvas.height) % canvas.height;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.15;
        ctx.fill();
      });
      requestAnimationFrame(draw);
    };
    draw();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section className="py-16 sm:py-24 lg:py-32 bg-[#050608] relative overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />
      
      {/* Luces de ambiente en azul y cian */}
      <div className="absolute top-1/2 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-[#1A73E8]/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#00C2FF]/5 blur-[100px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="text-center mb-14 sm:mb-20 lg:mb-24">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full backdrop-blur-md mb-6 sm:mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00C2FF] shadow-[0_0_8px_#00C2FF]"></span>
            <span className="text-white text-[11px] font-bold uppercase tracking-[0.2em]">Portafolio</span>
          </div>
          
          <h2 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tighter mb-5 sm:mb-8 leading-[1.1]">
            Soluciones que <br /> 
            <span className="text-[#00C2FF]">Desarrollamos.</span>
          </h2>
          
          <p className="text-base sm:text-lg lg:text-xl text-[#86868b] max-w-2xl mx-auto leading-relaxed px-2">
            Proyectos diseñados para transformar ideas en productos digitales funcionales, escalables y eficientes.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
          {projects.map((project, index) => (
            <div 
              key={index}
              className="group p-6 sm:p-8 lg:p-10 rounded-[2rem] sm:rounded-[2.5rem] bg-[#0d1117]/60 border border-white/5 hover:border-[#00C2FF]/40 transition-all duration-500 backdrop-blur-xl shadow-2xl flex flex-col relative overflow-hidden"
            >
              {/* Brillo sutil en el borde superior */}
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:via-[#00C2FF]/30 transition-all duration-700" />

              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-xl sm:rounded-2xl bg-[#00C2FF]/10 border border-[#00C2FF]/20 mb-6 sm:mb-10 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                <i className={`${project.icon} text-xl sm:text-2xl text-[#00C2FF]`}></i>
              </div>

              <p className="text-[#86868b] text-sm sm:text-base leading-relaxed mb-8 sm:mb-12 flex-grow group-hover:text-white/80 transition-colors">
                "{project.content}"
              </p>

              <div className="mt-auto pt-6 sm:pt-8 border-t border-white/5">
                <div className="text-white font-bold text-base sm:text-lg mb-1 group-hover:text-[#00C2FF] transition-colors">
                  {project.name}
                </div>
                <div className="text-xs font-black text-[#00C2FF]/60 uppercase tracking-widest">
                  {project.role}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}